import React from 'react'
import {
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { Link as Linkreact } from 'react-router-dom'

const EventsTable = ({
  events,
  loading,
  paginatedEvents,
  page,
  totalPages,
  onPageChange,
  getPageNumbers,
}) => {
  return (
    <Paper
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 1,
        overflow: 'hidden',
        boxShadow: (theme) =>
          theme.palette.mode === 'light'
            ? '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)'
            : '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.4)',
      }}>
      <Table
        sx={{
          minWidth: 650,
        }}
        aria-label="events table">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === 'light' ? 'rgba(245, 247, 250, 1)' : 'rgba(18, 18, 18, 1)',
            }}>
            <TableCell
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                py: { xs: 1.25, sm: 1.5, md: 1.75 },
                px: { xs: 2, sm: 2.5, md: 3 },
              }}>
              Event Name
            </TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                py: { xs: 1.25, sm: 1.5, md: 1.75 },
                px: { xs: 2, sm: 2.5, md: 3 },
              }}>
              # of Participants
            </TableCell>
            <TableCell
              align="center"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                py: { xs: 1.25, sm: 1.5, md: 1.75 },
                px: { xs: 1.5, sm: 2, md: 2.5 },
                width: { xs: 'auto', sm: 100, md: 120 },
              }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedEvents.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={3}
                align="center"
                sx={{
                  py: { xs: 4, sm: 5, md: 6 },
                  color: 'text.secondary',
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                }}>
                {loading ? 'Loading...' : 'No events found'}
              </TableCell>
            </TableRow>
          ) : (
            paginatedEvents.map((row, index) => (
              <TableRow
                key={row._id}
                sx={{
                  backgroundColor: (theme) =>
                    index % 2 === 0
                      ? theme.palette.mode === 'light'
                        ? 'rgba(255, 255, 255, 1)'
                        : 'rgba(18, 18, 18, 1)'
                      : theme.palette.mode === 'light'
                        ? 'rgba(249, 250, 251, 1)'
                        : 'rgba(30, 30, 30, 1)',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(245, 245, 245, 1)'
                        : 'rgba(40, 40, 40, 1)',
                  },
                  transition: (theme) =>
                    theme.transitions.create(['background-color'], {
                      duration: theme.transitions.duration.short,
                    }),
                }}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    color: 'text.primary',
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    py: { xs: 1.25, sm: 1.5, md: 1.75 },
                    px: { xs: 2, sm: 2.5, md: 3 },
                    fontWeight: 400,
                    lineHeight: 1.5,
                  }}>
                  <Linkreact
                    to={`/event/${row._id}`}
                    style={{
                      textDecoration: 'none',
                    }}>
                    <Typography
                      component="span"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 500,
                        '&:hover': {
                          color: 'primary.main',
                        },
                        transition: (theme) =>
                          theme.transitions.create(['color'], {
                            duration: theme.transitions.duration.short,
                          }),
                        cursor: 'pointer',
                        display: 'inline-block',
                      }}>
                      {row.name}
                    </Typography>
                  </Linkreact>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: 'text.primary',
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    py: { xs: 1.25, sm: 1.5, md: 1.75 },
                    px: { xs: 2, sm: 2.5, md: 3 },
                    fontWeight: 400,
                    lineHeight: 1.5,
                  }}>
                  {row.participantCount ? row.participantCount : 'Not Found'}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    py: { xs: 1.25, sm: 1.5, md: 1.75 },
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: { xs: 0.5, sm: 0.75, md: 1 },
                    }}>
                    {/* Edit Button */}
                    <Tooltip title="Edit" arrow>
                      <IconButton
                        component={Linkreact}
                        to={`/event/${row._id}/edit`}
                        size="small"
                        sx={{
                          color: 'primary.main',
                          backgroundColor: 'transparent',
                          '&:hover': {
                            backgroundColor: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'rgba(25, 118, 210, 0.08)'
                                : 'rgba(144, 202, 249, 0.08)',
                          },
                          width: { xs: 32, sm: 36, md: 40 },
                          height: { xs: 32, sm: 36, md: 40 },
                          transition: (theme) =>
                            theme.transitions.create(['background-color'], {
                              duration: theme.transitions.duration.short,
                            }),
                        }}>
                        <EditIcon
                          sx={{
                            fontSize: {
                              xs: '1rem',
                              sm: '1.125rem',
                              md: '1.25rem',
                            },
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Custom Pagination */}
      {paginatedEvents.length > 0 && totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 0.75, sm: 1, md: 1.5 },
            py: { xs: 2.5, sm: 3, md: 3.5 },
            px: { xs: 2, sm: 2.5, md: 3 },
            borderTop: '1px solid',
            borderColor: 'divider',
          }}>
          {getPageNumbers().map((pageNum, index) => {
            if (pageNum === 'ellipsis') {
              return (
                <Typography
                  key={`ellipsis-${index}`}
                  sx={{
                    color: 'text.secondary',
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    px: { xs: 0.75, sm: 1, md: 1.5 },
                    fontWeight: 400,
                  }}>
                  ...
                </Typography>
              )
            }

            const isActive = pageNum === page + 1

            return (
              <IconButton
                key={pageNum}
                onClick={() => onPageChange(null, pageNum - 1)}
                sx={{
                  minWidth: { xs: 32, sm: 36, md: 40 },
                  width: { xs: 32, sm: 36, md: 40 },
                  height: { xs: 32, sm: 36, md: 40 },
                  backgroundColor: isActive ? 'primary.main' : 'transparent',
                  color: isActive ? 'primary.contrastText' : 'primary.main',
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  fontWeight: isActive ? 600 : 500,
                  '&:hover': {
                    backgroundColor: isActive
                      ? 'primary.dark'
                      : (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.04)',
                  },
                  borderRadius: 1,
                  transition: (theme) =>
                    theme.transitions.create(['background-color', 'color'], {
                      duration: theme.transitions.duration.short,
                    }),
                }}>
                {pageNum}
              </IconButton>
            )
          })}
        </Box>
      )}
    </Paper>
  )
}

export default EventsTable
